"use client";

import { Column } from "@faire/web--source/slate/Layout";
import { IFilterablePaginatedSection } from "@faire/web-api--source/indigofair/data/IFilterablePaginatedSection";
import React from "react";

import { SDUIComponentRenderer } from "@faire/retailer/components/SDUI/__internal__/SDUIComponentRenderer/SDUIComponentRenderer";
import { SDUISectionInstanceRendererProps } from "@faire/retailer/components/SDUI/__internal__/types";
import { SDUIFilterRowRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/FilterBar/SDUIFilterRowRenderer";
import { useFilterablePaginatedSectionQuery } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useFilterablePaginatedSectionQuery";
import { hubComponentRenderers } from "@faire/retailer/views/category/HubsRSC/utils/hubComponentRenderers";

import { SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton } from "../LoadingSkeleton/SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton";
import { SDUIFilterablePaginatedSectionGridLoadingSkeleton } from "../LoadingSkeleton/SDUIFilterablePaginatedSectionGridLoadingSkeleton";

export const SDUIFilterablePaginatedSectionComponentsColumnNonRSC: React.FC<
  Omit<SDUISectionInstanceRendererProps, "section"> & {
    filterablePaginatedSection: IFilterablePaginatedSection;
  }
> = ({
  filterablePaginatedSection,
  componentRenderers,
  sectionLoggingData,
}) => {
  const {
    isFilterLoading,
    isFilteredComponentsFetching,
    filteredComponentsData,
    isFilteredComponentsError,
  } = useFilterablePaginatedSectionQuery(filterablePaginatedSection);

  const filteredComponents = filteredComponentsData?.components;
  return (
    <Column style={{ width: "100%", minWidth: 0 }}>
      {filteredComponents ? (
        filteredComponents
          .filter((component) => component.filter_bar_component)
          .map((component) => {
            return (
              <SDUIFilterRowRenderer
                key={component.id}
                component={component}
                sectionLoggingParams={sectionLoggingData}
                isFilterLoading={isFilterLoading}
              />
            );
          })
      ) : (
        <SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton />
      )}

      {!isFilteredComponentsFetching && !isFilteredComponentsError ? (
        filteredComponents
          ?.filter((component) => !component.filter_bar_component)
          .map((component) => (
            <SDUIComponentRenderer
              key={component.id}
              component={component}
              componentRenderers={getComponentRenderers(componentRenderers)}
              sectionLoggingParams={sectionLoggingData}
              isPrimaryContent={!!component.is_required_for_interactivity}
              renderBehaviourOverrides={{
                gridOptimizations: true,
              }}
            />
          ))
      ) : (
        <SDUIFilterablePaginatedSectionGridLoadingSkeleton />
      )}
    </Column>
  );
};

/**
 * This is a temporary fix since we cannot pass functions from the server to the FPS section renderer
 * (which is a client component)
 * This will be removed once we roll out RSC fully for FPS
 * see HubsViewLayoutRendererRSC.tsx for the other half of this
 */
const getComponentRenderers = (
  componentRenderers: SDUISectionInstanceRendererProps["componentRenderers"]
) => {
  return Object.keys(componentRenderers).length > 0
    ? componentRenderers
    : hubComponentRenderers;
};
